import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserRole } from '@/entities/user/model/userSchema';
import { mockDelay } from '@/shared/api/mockDelay';
import { appendAuditEntry } from '@/shared/api/auditStorage';
import { roleLabelFromCode } from '@/shared/constants/userRoles';
import { addUserRecord, mergeUserRecords, updateUserRecord } from '@/shared/api/usersPatchStorage';
import { userRecords } from './mockData';
import { User } from '@/entities/auth/model/authSchema';
import { Session } from '@/entities/auth/model/sessionSchema';
/** GET /api/admin/users */
export function useUserRecords() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => mockDelay(mergeUserRecords(userRecords)),
  });
}

/** POST /api/admin/users */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof addUserRecord>[0]) => {
      const row = addUserRecord(input);
      await mockDelay(null);
      const actor =
        queryClient.getQueryData<{ session: Session; user: User } | null>(['auth', 'session'])?.user.email ?? 'system';
      appendAuditEntry({
        user: actor,
        action: `Создан пользователь ${row.adLogin} (${roleLabelFromCode(row.roleCode)})`,
      });
      return row;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'auditLog'] });
    },
  });
}

/** PATCH /api/admin/users/:id */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number; name: string; orgUnit: string; roleCode: UserRole; adLogin: string }) => {
      updateUserRecord(vars.id, {
        name: vars.name,
        orgUnit: vars.orgUnit,
        roleCode: vars.roleCode,
      });
      await mockDelay(null);
      const actor =
        queryClient.getQueryData<{ session: Session; user: User } | null>(['auth', 'session'])?.user.email ?? 'system';
      appendAuditEntry({
        user: actor,
        action: `Изменены данные/роль пользователя ${vars.adLogin}`,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'auditLog'] });
    },
  });
}
